import * as Pages from 'pages';
import { Authentication } from 'utils';
import { ReactNode, useEffect, useState } from 'react';
import { Layout, NewYearSnowOverlay } from 'components';
import { useLanguageContext } from 'providers/language';
import { useSnowSettings } from 'providers/snow-settings';
import { isNewYearSnowDayTR } from 'components/new-year-snow-overlay';
import { Routes, Navigate, Route, BrowserRouter } from 'react-router-dom';

const App = () => {
  // useState hooks
  const { enabled, color } = useSnowSettings();
  const [showSnow, setShowSnow] = useState(() => isNewYearSnowDayTR());

  // dynamic variables
  const { language } = useLanguageContext();
  const hasSession = Authentication.hasSession();
  const redirect = hasSession ? '/dashboard' : '/login';

  // helper functions
  const renderPublicRoute = (permission: boolean, component: ReactNode) => {
    return !permission ? component : <Navigate to="/dashboard" />;
  };

  const renderPrivateRoute = (permission: boolean, component: ReactNode) => {
    return permission ? component : <Navigate to="/login" />;
  };

  // useEffect hooks
  useEffect(() => {
    document.title = `Idecon | ${language === 'tr' ? 'Proje Takip Sistemi' : 'Project Tracking System'}`;
  }, [language]);

  useEffect(() => {
    const id = window.setInterval(() => setShowSnow(isNewYearSnowDayTR()), 60_000);

    return () => window.clearInterval(id);
  }, []);

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          {/* All Time Routes */}
          <Route path="/confirm-effort/:guid" element={<Pages.ConfirmEffort />} />

          {/* Public Routes */}
          <Route path="/login" element={renderPublicRoute(hasSession, <Pages.Login />)} />
          <Route path="/sign-up" element={renderPublicRoute(hasSession, <Pages.SignUp />)} />
          <Route path="/forgot-password" element={renderPublicRoute(hasSession, <Pages.ForgotPassword />)} />

          {/* Private Routes */}
          <Route
            path="/project-step-managing"
            element={renderPrivateRoute(hasSession, <Pages.ProjectStepManaging />)}
          />
          <Route
            path="/project-plan-generate"
            element={renderPrivateRoute(hasSession, <Pages.ProjectPlanGenerate />)}
          />
          <Route
            path="/customer-source-types"
            element={renderPrivateRoute(hasSession, <Pages.CustomerSourceTypes />)}
          />
          <Route
            path="/effort-status-contacts"
            element={renderPrivateRoute(hasSession, <Pages.EffortStatusContacts />)}
          />
          <Route path="/users" element={renderPrivateRoute(hasSession, <Pages.Users />)} />
          <Route path="/roles" element={renderPrivateRoute(hasSession, <Pages.Roles />)} />
          <Route path="/report" element={renderPrivateRoute(hasSession, <Pages.Report />)} />
          <Route path="/book" element={renderPrivateRoute(hasSession, <Pages.CreateBook />)} />
          <Route path="/profile" element={renderPrivateRoute(hasSession, <Pages.Profile />)} />
          <Route path="/projects" element={renderPrivateRoute(hasSession, <Pages.Projects />)} />
          <Route path="/timeline" element={renderPrivateRoute(hasSession, <Pages.Timeline />)} />
          <Route path="/gib-types" element={renderPrivateRoute(hasSession, <Pages.GibTypes />)} />
          <Route path="/dashboard" element={renderPrivateRoute(hasSession, <Pages.Dashboard />)} />
          <Route path="/used-banks" element={renderPrivateRoute(hasSession, <Pages.UsedBanks />)} />
          <Route path="/activity-types" element={renderPrivateRoute(hasSession, <Pages.ActivityTypes />)} />
          <Route path="/effort-tracing" element={renderPrivateRoute(hasSession, <Pages.EffortTracing />)} />
          <Route path="/product-report" element={renderPrivateRoute(hasSession, <Pages.ProductReport />)} />
          <Route path="/project-groups" element={renderPrivateRoute(hasSession, <Pages.ProjectGroups />)} />
          <Route path="/effort-timeline" element={renderPrivateRoute(hasSession, <Pages.EffortTimeline />)} />
          <Route path="/effort-contacts" element={renderPrivateRoute(hasSession, <Pages.EffortContacts />)} />
          <Route path="/connection-types" element={renderPrivateRoute(hasSession, <Pages.ConnectionTypes />)} />
          <Route path="/sales-stage-types" element={renderPrivateRoute(hasSession, <Pages.SalesStageTypes />)} />
          <Route path="/personnel-holiday" element={renderPrivateRoute(hasSession, <Pages.PersonnelHoliday />)} />
          <Route path="/environment-types" element={renderPrivateRoute(hasSession, <Pages.EnvironmentTypes />)} />
          <Route path="/activity-managing" element={renderPrivateRoute(hasSession, <Pages.ActivityManaging />)} />
          <Route path="/holiday-monitoring" element={renderPrivateRoute(hasSession, <Pages.HolidayMonitoring />)} />
          <Route path="/project-monitoring" element={renderPrivateRoute(hasSession, <Pages.ProjectMonitoring />)} />
          <Route path="/holiday-yearly-view" element={renderPrivateRoute(hasSession, <Pages.HolidayYearlyView />)} />
          <Route path="/freshdesk-dashboard" element={renderPrivateRoute(hasSession, <Pages.FreshdeskDashboard />)} />
          <Route path="/activity-monitoring" element={renderPrivateRoute(hasSession, <Pages.ActivityMonitoring />)} />
          <Route path="/product-project-plan" element={renderPrivateRoute(hasSession, <Pages.ProductProjectPlan />)} />

          {/* Redirecting from path "/" */}
          <Route path="/*" element={<Navigate to={redirect} />} />
        </Routes>
      </Layout>
      {showSnow && enabled && <NewYearSnowOverlay color={color} />}
    </BrowserRouter>
  );
};

export default App;
